/**
 * Style of generic elements (drawing symbols): lines, text and markers
 * (icon with text). Stored per item in `camera_plan_items.style`.
 */
import { DEFAULT_MARKER_ICON, getMarkerIcon } from "./marker-icons.js";
import { SYMBOL_INK, getSymbolType } from "./symbols.js";
export const DRAW_COLORS = [
    { value: "#FFFFFF", label: "White" },
    { value: "#FFB224", label: "Amber" },
    { value: "#FF5A4E", label: "Red" },
    { value: "#3DDC84", label: "Green" },
    { value: "#3FD3EA", label: "Cyan" },
    { value: "#4C8DFF", label: "Blue" },
    { value: "#C39BFF", label: "Violet" },
    { value: "#10161D", label: "Black" },
];
export const LINE_DASHES = ["solid", "dashed", "dotted"];
export const LINE_WIDTH_MIN = 1;
export const LINE_WIDTH_MAX = 12;
export const TEXT_SIZE_MIN = 10;
export const TEXT_SIZE_MAX = 48;
const DEFAULTS = {
    line: {
        color: "#FF5A4E",
        dash: "solid",
        width: 3,
        arrow: false,
        size: 16,
        icon: DEFAULT_MARKER_ICON,
    },
    text: {
        color: "#FFFFFF",
        dash: "solid",
        width: 3,
        arrow: false,
        size: 16,
        icon: DEFAULT_MARKER_ICON,
    },
    marker: {
        color: "#FFFFFF",
        dash: "solid",
        width: 3,
        arrow: false,
        size: 16,
        icon: DEFAULT_MARKER_ICON,
    },
};
const HEX = /^#[0-9a-fA-F]{6}$/;
function clamp(n, min, max) {
    const v = Number(n);
    return Number.isFinite(v) ? Math.max(min, Math.min(max, v)) : undefined;
}
/** Validate a stored style; unknown or invalid fields are dropped. */
export function sanitizeItemStyle(value) {
    if (!value || typeof value !== "object")
        return {};
    const v = value;
    const out = {};
    if (typeof v.color === "string" && HEX.test(v.color)) {
        out.color = v.color.toUpperCase();
    }
    if (LINE_DASHES.includes(v.dash))
        out.dash = v.dash;
    const width = clamp(v.width, LINE_WIDTH_MIN, LINE_WIDTH_MAX);
    if (width !== undefined)
        out.width = width;
    if (typeof v.arrow === "boolean")
        out.arrow = v.arrow;
    const size = clamp(v.size, TEXT_SIZE_MIN, TEXT_SIZE_MAX);
    if (size !== undefined)
        out.size = Math.round(size);
    if (typeof v.icon === "string" && getMarkerIcon(v.icon).key === v.icon) {
        out.icon = v.icon;
    }
    return out;
}
/** Style of an item with the defaults of its symbol filled in. */
export function resolveItemStyle(item) {
    const key = getSymbolType(item.symbolType).key;
    return {
        ...(DEFAULTS[key] ?? DEFAULTS.marker),
        ...sanitizeItemStyle(item.style),
    };
}
/** Whether dark ink reads better than white on a colour. */
export function isLightColor(hex) {
    const n = parseInt(hex.slice(1), 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
    return 0.299 * r + 0.587 * g + 0.114 * b > 150;
}
/** Map image id of a marker chip: icon and colour. */
export function markerImageId(icon, color) {
    return `marker:${getMarkerIcon(icon).key}:${color.replace("#", "").toUpperCase()}`;
}
/** Parse a markerImageId back into icon and colour. */
export function parseMarkerImageId(id) {
    const match = /^marker:([a-z0-9-]+):([0-9A-F]{6})$/.exec(id);
    return match ? { icon: match[1], color: `#${match[2]}` } : null;
}
/** Round chip of a marker: its colour, with the icon in contrasting ink. */
export function markerChipSvg(icon, color, size = 30) {
    const r = 12;
    const ink = isLightColor(color) ? SYMBOL_INK : "#FFFFFF";
    const ring = isLightColor(color) ? "#fff" : "rgba(255,255,255,.85)";
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="-15 -15 30 30"><circle r="${r * 1.09}" fill="none" stroke="rgba(0,0,0,.5)" stroke-width="${r * 0.18}"/><circle r="${r}" fill="${color}" stroke="${ring}" stroke-width="${r * 0.17}"/><g transform="translate(-8.7 -8.7) scale(0.725)" fill="none" stroke="${ink}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${getMarkerIcon(icon).glyph}</g></svg>`;
}
